import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Plus, X, Search, RefreshCw, CheckCircle2 } from "lucide-react";
import { useGeneric } from "../../hooks/useGeneric";
import { getCurrentUser } from "../../utils/session";
import { formatDateTime, formatAmcStatus } from "../../utils/crmHelpers";
import { matchesAgency } from "../../utils/agencySearch";
import "../../styles/crm.css";

const STATUS_TABS = [
    { key: "open", label: "Open" },
    { key: "in_progress", label: "In Progress" },
    { key: "closed", label: "Closed" },
    { key: "all", label: "All" },
];

const ServiceCalls = () => {
    const currentUser = getCurrentUser();

    const [tab, setTab] = useState("open");
    const [search, setSearch] = useState("");

    const [showNewForm, setShowNewForm] = useState(false);
    const [clientSearch, setClientSearch] = useState("");
    const [pickedAgency, setPickedAgency] = useState(null);
    const [complaintId, setComplaintId] = useState("");
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);

    const [selectedCall, setSelectedCall] = useState(null);
    const [noteText, setNoteText] = useState("");
    const [logging, setLogging] = useState(false);

    const {
        data: serviceCalls = [], loading, fetchAll: fetchServiceCalls, save: saveServiceCall,
    } = useGeneric({ tableName: "crm_service_calls", idColumn: "service_call_id", removeOnInsert: ["service_call_id"] });

    const { data: agencies = [], fetchAll: fetchAgencies } = useGeneric({
        tableName: "agencies", idColumn: "agency_id", orderBy: "agency_name",
    });
    const { data: complaints = [], fetchAll: fetchComplaints } = useGeneric({
        tableName: "complaintmaster", idColumn: "complaint_id", orderBy: "complaint_name",
    });
    const { data: executives = [], fetchAll: fetchExecutives } = useGeneric({
        tableName: "executives", idColumn: "executive_id", orderBy: "executive_name",
    });

    const {
        data: notes = [], fetchAll: fetchNotes, save: saveNote,
    } = useGeneric({
        tableName: "crm_activities", idColumn: "activity_id", orderBy: "activity_date",
        filters: selectedCall ? { service_call_id: selectedCall.service_call_id } : undefined,
    });

    useEffect(() => { fetchServiceCalls(); fetchAgencies(); fetchComplaints(); fetchExecutives(); },
        [fetchServiceCalls, fetchAgencies, fetchComplaints, fetchExecutives]);
    useEffect(() => { if (selectedCall) fetchNotes(); }, [selectedCall, fetchNotes]);

    const agencyName = useCallback(
        (id) => agencies.find((a) => Number(a.agency_id) === Number(id))?.agency_name ?? "—",
        [agencies]
    );
    const complaintName = (id) => complaints.find((c) => Number(c.complaint_id) === Number(id))?.complaint_name ?? "—";
    const executiveName = (id) => executives.find((x) => Number(x.executive_id) === Number(id))?.executive_name ?? "—";

    const filteredCalls = useMemo(() => {
        let rows = serviceCalls;
        if (tab !== "all") rows = rows.filter((c) => c.status === tab);
        if (search.trim()) {
            rows = rows.filter((c) => {
                const agency = agencies.find((a) => Number(a.agency_id) === Number(c.client_id));
                return matchesAgency(agency, search);
            });
        }
        return rows.slice().sort((a, b) => new Date(b.opened_at) - new Date(a.opened_at));
    }, [serviceCalls, agencies, tab, search]);

    const matchingAgencies = agencies.filter((a) => matchesAgency(a, clientSearch)).slice(0, 8);

    const openNew = () => {
        setPickedAgency(null); setClientSearch(""); setComplaintId(""); setDescription("");
        setShowNewForm(true);
    };
    const closeNew = () => setShowNewForm(false);

    const onCreateCall = async () => {
        if (!pickedAgency) { alert("Select a client first."); return; }
        setSaving(true);
        try {
            await saveServiceCall({
                client_id: pickedAgency.agency_id,
                complaint_id: complaintId || null,
                description,
                status: "open",
                opened_by: currentUser?.executive_id ?? null,
            });
            closeNew();
        } catch (err) {
            alert("Could not create service call: " + err.message);
        } finally {
            setSaving(false);
        }
    };

    const onLogNote = async () => {
        if (!selectedCall || !noteText.trim()) return;
        setLogging(true);
        try {
            await saveNote({
                service_call_id: selectedCall.service_call_id,
                client_id: selectedCall.client_id,
                user_id: currentUser?.executive_id ?? null,
                notes: noteText,
                outcome: "service",
            });
            setNoteText("");
            fetchNotes();
        } catch (err) {
            alert("Could not save note: " + err.message);
        } finally {
            setLogging(false);
        }
    };

    const onChangeStatus = async (status) => {
        if (!selectedCall) return;
        const updated = { ...selectedCall, status, closed_at: status === "closed" ? new Date().toISOString() : null };
        await saveServiceCall(updated);
        setSelectedCall(updated);
        fetchServiceCalls();
    };

    const agencyDetail = selectedCall ? agencies.find((a) => Number(a.agency_id) === Number(selectedCall.client_id)) : null;
    const amcInfo = agencyDetail ? formatAmcStatus(agencyDetail.amc_valid_upto) : null;

    return (
        <div className="crm-wrap">
            <div className="crm-toolbar">
                <div className="crm-tabs">
                    {STATUS_TABS.map((t) => (
                        <button key={t.key} className={`crm-tab${tab === t.key ? " active" : ""}`} onClick={() => setTab(t.key)}>
                            {t.label}
                        </button>
                    ))}
                </div>
                <div className="crm-toolbar-actions">
                    <div className="crm-search-wrapper">
                        <Search size={15} className="crm-search-icon" />
                        <input className="crm-search" placeholder="Search by name, software ID, phone…" value={search}
                            onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <button className="crm-btn-icon" onClick={fetchServiceCalls} title="Refresh"><RefreshCw size={16} /></button>
                    <button className="crm-btn-primary" onClick={openNew}><Plus size={16} /> New Service Call</button>
                </div>
            </div>

            <div className="crm-table-wrap">
                <table className="crm-table">
                    <thead><tr><th>Client</th><th>Issue Type</th><th>Status</th><th>Opened</th><th>Opened By</th></tr></thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="crm-empty">Loading…</td></tr>
                        ) : filteredCalls.length === 0 ? (
                            <tr><td colSpan={5} className="crm-empty">No service calls found.</td></tr>
                        ) : filteredCalls.map((c) => (
                            <tr key={c.service_call_id} className="crm-row" onClick={() => setSelectedCall(c)}>
                                <td><strong>{agencyName(c.client_id)}</strong></td>
                                <td>{complaintName(c.complaint_id)}</td>
                                <td><span className={`crm-badge crm-badge-sc-${c.status}`}>{c.status.replace("_", " ")}</span></td>
                                <td>{formatDateTime(c.opened_at)}</td>
                                <td>{executiveName(c.opened_by)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* ── New Service Call Modal ─────────────────────────── */}
            {showNewForm && (
                <div className="crm-modal-overlay">
                    <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="crm-modal-header"><h3>New Service Call</h3>
                            <button className="crm-icon-btn" onClick={closeNew}><X size={18} /></button>
                        </div>
                        <div className="crm-modal-body">
                            <div className="crm-field">
                                <label>Client</label>
                                {pickedAgency ? (
                                    <div className="crm-picked-client">
                                        <span>{pickedAgency.agency_name} · {pickedAgency.mobile}</span>
                                        <button className="crm-icon-btn" onClick={() => setPickedAgency(null)}><X size={14} /></button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="crm-search-wrapper">
                                            <Search size={14} className="crm-search-icon" />
                                            <input className="crm-search" placeholder="Search by name, software ID, phone, mobile…"
                                                value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} />
                                        </div>
                                        {clientSearch && (
                                            <div className="crm-multiselect">
                                                {matchingAgencies.length === 0 ? (
                                                    <div className="crm-empty-inline">No matches</div>
                                                ) : matchingAgencies.map((a) => (
                                                    <div key={a.agency_id} className="crm-option" onClick={() => setPickedAgency(a)}>
                                                        {a.agency_name} <span className="crm-subtext">· {a.mobile}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {pickedAgency && (
                                <div className="crm-client-summary">
                                    <div><strong>Software:</strong> {pickedAgency.software_key || "—"}</div>
                                    <div><strong>AMC:</strong> {formatAmcStatus(pickedAgency.amc_valid_upto).text}</div>
                                    <div><strong>Machines:</strong> {pickedAgency.client_nos ?? 0}</div>
                                </div>
                            )}

                            <div className="crm-field">
                                <label>Issue Type</label>
                                <select value={complaintId} onChange={(e) => setComplaintId(e.target.value)}>
                                    <option value="">— Select —</option>
                                    {complaints.map((c) => <option key={c.complaint_id} value={c.complaint_id}>{c.complaint_name}</option>)}
                                </select>
                            </div>
                            <div className="crm-field">
                                <label>Description</label>
                                <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
                            </div>
                        </div>
                        <div className="crm-modal-footer">
                            <button className="crm-btn-secondary" onClick={closeNew}>Cancel</button>
                            <button className="crm-btn-primary" disabled={saving} onClick={onCreateCall}>
                                {saving ? "Saving…" : "Create"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Service Call Detail Modal ──────────────────────── */}
            {selectedCall && (
                <div className="crm-modal-overlay">
                    <div className="crm-modal crm-modal-wide" onClick={(e) => e.stopPropagation()}>
                        <div className="crm-modal-header">
                            <div>
                                <h3>{agencyName(selectedCall.client_id)}</h3>
                                <div className="crm-subtext">{complaintName(selectedCall.complaint_id)}</div>
                            </div>
                            <button className="crm-icon-btn" onClick={() => setSelectedCall(null)}><X size={18} /></button>
                        </div>
                        <div className="crm-modal-body crm-workspace">
                            <div className="crm-workspace-main">
                                {agencyDetail && (
                                    <div className="crm-client-summary">
                                        <div><strong>Software:</strong> {agencyDetail.software_key || "—"}</div>
                                        <div>
                                            <strong>AMC:</strong>{" "}
                                            <span className={`crm-flag ${amcInfo.urgency === "expired" ? "danger" : amcInfo.urgency === "critical" ? "danger" : amcInfo.urgency === "warning" ? "warn" : "ok"}`}>
                                                {amcInfo.text}
                                            </span>
                                        </div>
                                        <div><strong>Machines:</strong> {agencyDetail.client_nos ?? 0}</div>
                                        <div><strong>Place:</strong> {agencyDetail.place || "—"}</div>
                                    </div>
                                )}

                                <div className="crm-status-row">
                                    <span className={`crm-badge crm-badge-sc-${selectedCall.status}`}>{selectedCall.status.replace("_", " ")}</span>
                                    {selectedCall.status !== "in_progress" && (
                                        <button className="crm-btn-secondary sm" onClick={() => onChangeStatus("in_progress")}>Mark In Progress</button>
                                    )}
                                    {selectedCall.status !== "closed" && (
                                        <button className="crm-btn-primary sm" onClick={() => onChangeStatus("closed")}>
                                            <CheckCircle2 size={13} /> Close Call
                                        </button>
                                    )}
                                </div>

                                <div className="crm-log-call">
                                    <h4>Add Conversation</h4>
                                    <textarea rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="What was discussed…" />
                                    <button className="crm-btn-primary" disabled={logging || !noteText.trim()} onClick={onLogNote}>
                                        {logging ? "Saving…" : "Save Note"}
                                    </button>
                                </div>
                            </div>

                            <div className="crm-workspace-timeline">
                                <h4>Conversation Log</h4>
                                {notes.length === 0 ? (
                                    <div className="crm-empty-inline">No notes yet.</div>
                                ) : notes.slice().reverse().map((n) => (
                                    <div key={n.activity_id} className="crm-timeline-item">
                                        <div className="crm-timeline-date">{formatDateTime(n.activity_date)}</div>
                                        {n.notes && <div className="crm-timeline-notes">{n.notes}</div>}
                                        <div className="crm-timeline-meta">{executiveName(n.user_id)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ServiceCalls;